import {Section, Box, Grid, MediaPlaylist, Image} from '@primer/react-brand/esm'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import type {DetailSectionProps} from './types'
import {renderWithoutParagraphs} from '../../../utils'
import {FlexSuiteRiverAccordion} from '../../../components/FlexSuiteRiverAccordion'
import {FlexSuiteRiverStack} from '../../../components/FlexSuiteRiverStack'
import {FlexSuiteRiverFootsteps} from '../../../components/FlexSuiteRiverFootsteps'
import {FlexSuiteIconCards} from '../../../components/FlexSuiteIconCards'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {FlexSuiteSectionBreakout} from '../../../components/FlexSuiteSectionBreakout'
import {FlexSuiteSectionIntro} from '../../../components/FlexSuiteSectionIntro'
import {AssetTypeEnum} from '../../../types'
import {AssetVideo} from '../../../components/Video'
import {getImageSources} from '@github-ui/swp-core/lib/utils/images'
import {slugify} from '@github-ui/swp-core/lib/utils/slugify'

export function DetailSection({
  sectionIntroHeading,
  sectionIntroLabel,
  sectionIntroDescription,
  sectionIntroLink,
  riverVariant,
  disableDither,
  featureRiverAccordion,
  featureRiverStack,
  breakoutVariant,
  featureRiverBreakout,
  featureBreakoutTabs,
  cardsVariant,
  featureFootstepsLabel,
  featureFootstepsHeading,
  featureFootstepsLink,
  featureFootstepsCards,
  featureLargeCardsHeading,
  featureLargeCards,
  iconCardsHeading,
  iconCards,
  videoPlaylistHeading,
  videoPlaylistItems,
}: DetailSectionProps) {
  const sectionId = slugify(documentToPlainTextString(sectionIntroHeading)) || undefined

  return (
    <Section id={sectionId} fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      <FlexSuiteSectionIntro
        heading={sectionIntroHeading}
        label={sectionIntroLabel}
        description={sectionIntroDescription}
        link={sectionIntroLink}
        constrainWidth
      />

      {riverVariant === 'Accordion' && featureRiverAccordion ? (
        <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
          <Grid>
            <Grid.Column>
              <FlexSuiteRiverAccordion items={featureRiverAccordion} disableDither={disableDither} />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}

      {riverVariant === 'Stack' && featureRiverStack ? (
        <Section paddingBlockStart="none" paddingBlockEnd="none">
          <FlexSuiteRiverStack items={featureRiverStack} />
        </Section>
      ) : null}

      <FlexSuiteSectionBreakout
        breakoutVariant={breakoutVariant}
        featureRiverBreakout={featureRiverBreakout}
        featureBreakoutTabs={featureBreakoutTabs}
      />

      {cardsVariant === 'Footsteps' && featureFootstepsCards ? (
        <FlexSuiteRiverFootsteps
          heading={featureFootstepsHeading}
          label={featureFootstepsLabel}
          cards={featureFootstepsCards}
          cta={featureFootstepsLink && featureFootstepsLink.fields}
        />
      ) : null}

      {cardsVariant === 'Large Cards' && featureLargeCards?.length ? (
        <Box
          marginBlockStart={{narrow: 'none', regular: 48}}
          paddingBlockStart={{narrow: 'none', regular: 48}}
          paddingBlockEnd={48}
        >
          <FlexSuiteLargeCards heading={featureLargeCardsHeading} cards={featureLargeCards} />
        </Box>
      ) : null}

      {cardsVariant === 'Icon Cards' && iconCards && iconCards.length > 0 ? (
        <FlexSuiteIconCards heading={iconCardsHeading} cards={iconCards} />
      ) : null}

      {videoPlaylistItems && videoPlaylistItems.length > 0 ? (
        <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
          <Grid>
            <Grid.Column>
              <MediaPlaylist>
                {videoPlaylistHeading && (
                  <MediaPlaylist.Heading as="h3">{renderWithoutParagraphs(videoPlaylistHeading)}</MediaPlaylist.Heading>
                )}
                {videoPlaylistItems.map(item => {
                  const {
                    heading,
                    description,
                    assetType,
                    optionImage,
                    optionVideo,
                    optionPoster,
                    autoplay,
                    videoSource,
                  } = item.fields
                  const thumbnailUrl = optionPoster?.fields?.file?.url || optionImage?.fields?.file?.url

                  return (
                    <MediaPlaylist.Item
                      key={item.sys.id}
                      thumbnail={thumbnailUrl ? <Image src={thumbnailUrl} alt="" /> : undefined}
                    >
                      <MediaPlaylist.ItemHeading
                        description={description ? documentToPlainTextString(description) : undefined}
                      >
                        {documentToPlainTextString(heading)}
                      </MediaPlaylist.ItemHeading>
                      <MediaPlaylist.ItemContent />
                      <MediaPlaylist.ItemMedia>
                        {assetType === AssetTypeEnum.enum.Image && optionImage && (
                          <Image
                            as="picture"
                            src={`${optionImage.fields.file.url}?fm=webp`}
                            sources={getImageSources(optionImage.fields.file.url, {maxWidth: 720})}
                            alt={optionImage.fields.description ?? ''}
                          />
                        )}
                        <AssetVideo
                          assetType={assetType}
                          optionVideo={optionVideo}
                          optionPoster={optionPoster}
                          autoplay={autoplay}
                          videoSource={videoSource}
                        />
                      </MediaPlaylist.ItemMedia>
                    </MediaPlaylist.Item>
                  )
                })}
              </MediaPlaylist>
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
    </Section>
  )
}
